import { is } from 'ramda';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';

/**
 * A function for negating predicates
 * @param predicate
 */

export const not = <T>(predicate: (value: T) => boolean) => (value: T): boolean =>
  value && !predicate(value);

/**
 * Checks if a value is null, undefined, or empty
 * @param value
 */
export const isNilOrEmpty = <T>(value: T): boolean => isNil(value) || isEmpty(value);

/**
 * Checks if a value is null, undefined, or not number
 * @param value
 */
export const isNilOrNaN = <T>(value: T): boolean => isNil(value) || !is(Number, value);

/**
 * Chains two comparators, that is if the 2 elements are equal given the first comparator, use the second one to do the comparison.
 * @param first
 * @param second
 * @param firstComparator
 * @param secondComparator
 */
const chainComparators = <T>(
  first: T,
  second: T,
  firstComparator: (first: T, second: T) => number,
  secondComparator: (first: T, second: T) => number,
): ((first: T, second: T) => number) => {
  const diff = firstComparator(first, second);
  if (diff === 0) {
    return secondComparator;
  }
  return firstComparator;
};

/**
 * Recursive function used by sortWith.
 * @param first
 * @param second
 * @param currentComparator
 * @param currentComparatorIndex
 * @param comparators
 */
const sortWithRecursive = <T>(
  first: T,
  second: T,
  currentComparator: (first: T, second: T) => number,
  currentComparatorIndex: number,
  ...comparators: ((first: T, second: T) => number)[]
): ((first: T, second: T) => number) => {
  const index = currentComparatorIndex + 1;
  if (index > comparators.length) {
    return currentComparator;
  }
  return sortWithRecursive(
    first,
    second,
    chainComparators(first, second, currentComparator, comparators[index]),
    currentComparatorIndex + 1,
    ...comparators,
  );
};

/**
 * Sort an array with multiple comparators. If values are equal for the first comparator, use the second one for comparison, and so forth.
 * @param arrayToSort
 * @param comparators
 */
export const sortWith = <T>(
  arrayToSort: T[],
  ...comparators: ((first: T, second: T) => number)[]
): T[] =>
  arrayToSort
    .slice()
    .sort((first: T, second: T): number =>
      sortWithRecursive(first, second, comparators[0], 0, ...comparators)(first, second),
    );



// WEBPACK FOOTER //
// /codebuild/output/src891534606/src/modules/common/src/helper/global.ts